'use client'

import React, { useEffect, useMemo, useRef } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { HiChevronUpDown, HiMiniExclamationTriangle } from 'react-icons/hi2'
import { HiOutlineTag, HiXCircle } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import {
  Combobox,
  ComboboxContent,
  type ComboboxContentProps,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2/Combobox'
import { TEXT_INPUT_CLASSNAME } from '@/components/common/TextInputV2'
import { ColorIndicator } from '@/components/tags/ColorIndicator'
import { Tag as TagComponent } from '@/components/tags/Tag'

import { compareTagsByPosition } from '@/helpers/tags'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useTags } from '@/hooks/tags/useTags'

import type { PaginatedResponse } from '@/types/Api'
import type { Tag, TagType } from '@/types/Tag'

interface TagComboboxProps {
  value: string | null
  onChange: (value: string | null) => void
  comboboxContentProps?: ComboboxContentProps
}

interface TagComboboxMultiProps {
  value: string[]
  onChange: (value: string[]) => void
  comboboxContentProps?: ComboboxContentProps
  emptyLabel?: React.ReactNode
  selectedLabel?: React.ReactNode
  triggerClassName?: string
  triggerVariant?: 'button' | 'field'
  searchPlaceholder?: string
  tagType?: TagType
}

export interface TagComboboxFooterActions {
  clearActive?: boolean
  clearDisabled?: boolean
  clearHint?: string
  clearLabel?: string
  onCancel: () => void
  onClear: () => void
  onUndoClear?: () => void
  onSave: () => void
  saveDisabled?: boolean
  saveLabel?: string
  saveLoading?: boolean
}

interface TagComboboxContentProps {
  multiple?: boolean
  value: string[] | null
  includeClear?: boolean
  onChange: (value: string[] | null) => void
  comboboxContentProps?: ComboboxContentProps
  tagType?: TagType
  clearLabel?: string
  placeholder?: string
  footerActions?: TagComboboxFooterActions
}

const selectSortedTags = (data: InfiniteData<PaginatedResponse<Tag>>) => {
  return data.pages
    .map((page) => page.data)
    .flat()
    .sort(compareTagsByPosition)
}

export const TagComboboxContent: React.FC<TagComboboxContentProps> = ({
  multiple = false,
  value,
  includeClear = false,
  onChange,
  comboboxContentProps,
  tagType,
  clearLabel = 'Clear Tags',
  placeholder = 'Select tag...',
  footerActions,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tags, isLoading } = useTags({
    orgId: activeOrgId,
    tagType,
    selector: selectSortedTags,
  })

  const { className, ...rest } = comboboxContentProps ?? {}

  const handleClear = () => {
    if (multiple) {
      onChange([])
    } else {
      onChange(null)
    }
  }

  const showInlineClear = includeClear && !footerActions
  const clearActive = footerActions?.clearActive ?? false
  const lastNonEmptyValueRef = useRef<string[] | null>(null)

  useEffect(() => {
    if (multiple && value && value.length > 0) {
      lastNonEmptyValueRef.current = [...value]
    }
  }, [multiple, value])

  const handleFooterClear = () => {
    if (!footerActions) {
      return
    }

    if (clearActive) {
      if (lastNonEmptyValueRef.current?.length) {
        onChange([...lastNonEmptyValueRef.current])
        return
      }

      if (footerActions.onUndoClear) {
        footerActions.onUndoClear()
      }
      return
    }

    footerActions.onClear()
  }

  return (
    <ComboboxContent
      className={twMerge(
        'p-0 sm:max-w-[212px]',
        footerActions &&
          'w-[320px] max-w-[calc(100vw-1.5rem)] sm:max-w-[320px]',
        className,
      )}
      {...rest}
    >
      <ComboboxInput
        placeholder={placeholder}
        className="placeholder:text-[13px]"
      />
      <ComboboxList className="max-h-[min(320px,var(--radix-popover-content-available-height))]">
        <ComboboxEmpty className="text-[13px]">
          {isLoading ? 'Loading...' : 'No tags found.'}
        </ComboboxEmpty>
        {tags && (
          // the border-b acts as a separator
          <ComboboxGroup
            className={twMerge(
              showInlineClear &&
                'border-b border-primary-200 dark:border-primary-800',
            )}
          >
            {tags.map((tag) => {
              const isSelected = value?.includes(tag.id) ?? false
              return (
                <ComboboxSelectableItem
                  key={tag.id}
                  value={tag.name}
                  isSelected={isSelected}
                  onSelect={() => {
                    if (!multiple) {
                      onChange([tag.id])
                      return
                    }
                    const newValue = value ?? []
                    if (isSelected) {
                      onChange(newValue.filter((id) => id !== tag.id))
                    } else {
                      onChange([...newValue, tag.id])
                    }
                  }}
                >
                  <div className="flex min-w-0 max-w-48 items-center gap-x-2 truncate">
                    <ColorIndicator color={tag.color} />
                    <span className="min-w-0 truncate text-[13px] font-medium">
                      {tag.name}
                    </span>
                  </div>
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        )}
        {showInlineClear && (
          <ComboboxGroup>
            <ComboboxSelectableItem
              value="clear"
              isSelected={false}
              onSelect={handleClear}
            >
              <div className="-ml-[2px] flex items-center gap-x-[6px]">
                <HiXCircle
                  size={14}
                  className="text-primary-400 dark:text-primary-500"
                />
                <span className="text-[13px] font-medium">{clearLabel}</span>
              </div>
            </ComboboxSelectableItem>
          </ComboboxGroup>
        )}
      </ComboboxList>
      {multiple && footerActions ? (
        <div className="border-t border-primary-200/80 bg-primary-50/80 backdrop-blur-sm dark:border-primary-800 dark:bg-default-dark">
          {clearActive ? (
            <div className="flex items-start gap-2 border-b border-amber-200/80 bg-amber-50/90 px-2.5 py-2 text-[12px] font-medium text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
              <HiMiniExclamationTriangle
                size={14}
                className="mt-0.5 shrink-0 text-amber-500 dark:text-amber-300"
              />
              <span>
                {footerActions.clearHint ??
                  'All tags will be removed when you save.'}
              </span>
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-3 p-2">
            <Button
              type="button"
              variant="ghost-compact"
              onClick={handleFooterClear}
              disabled={footerActions.clearDisabled}
              className={twMerge(
                'rounded-md px-1.5 text-[13px] hover:bg-white/70 disabled:hover:bg-transparent',
                clearActive
                  ? 'border-transparent bg-amber-100 text-amber-900 shadow-none hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-200 dark:hover:bg-amber-500/15'
                  : 'text-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/80',
              )}
            >
              {clearActive
                ? 'Undo Clear'
                : (footerActions.clearLabel ?? 'Clear All')}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="soft-compact"
                onClick={footerActions.onCancel}
                disabled={footerActions.saveLoading}
                className="rounded-md bg-white/90 px-2.5 text-[13px] text-primary-700 dark:bg-primary-900/80 dark:text-primary-200"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="blue-compact"
                onClick={footerActions.onSave}
                disabled={
                  footerActions.saveDisabled || footerActions.saveLoading
                }
                isLoading={footerActions.saveLoading}
                className="min-w-[72px] rounded-md px-2.5 text-[13px] shadow-sm"
              >
                {footerActions.saveLabel ?? 'Save'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </ComboboxContent>
  )
}

export const TagCombobox: React.FC<TagComboboxProps> = ({
  comboboxContentProps = {
    align: 'start',
    side: 'bottom',
  },
  value,
  onChange,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tags } = useTags({
    orgId: activeOrgId,
  })

  const [open, setOpen] = React.useState(false)

  const selectedTag = tags?.find((tag) => tag.id === value)

  const handleChange = (value: string[] | null) => {
    if (value === null || value.length === 0) {
      onChange(null)
    } else {
      const tag = tags?.find((tag) => tag.id === value[0])
      if (tag) {
        onChange(tag.id)
      }
    }
    setOpen(false)
  }

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger>
        {selectedTag ? (
          <TagComponent tagId={selectedTag.id} />
        ) : (
          <span className="text-[13px]">+ Select tag</span>
        )}
      </ComboboxTrigger>
      {/*
        not sure why, but when using this component inside a Dialog,
        the Dialog would double render which would cause it to mount twice
        and thus do its animation twice; this is a workaround to delay
        mounting until actually needed;
        TODO: figure out why this is happening and fix it
        */}
      {open && (
        <TagComboboxContent
          value={value ? [value] : null}
          comboboxContentProps={comboboxContentProps}
          onChange={handleChange}
        />
      )}
    </Combobox>
  )
}

export const TagComboboxMulti: React.FC<TagComboboxMultiProps> = ({
  value,
  onChange,
  comboboxContentProps = {
    align: 'start',
    side: 'bottom',
  },
  emptyLabel,
  selectedLabel,
  triggerClassName,
  triggerVariant = 'button',
  searchPlaceholder,
  tagType,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tags } = useTags({
    orgId: activeOrgId,
    tagType,
  })

  const [open, setOpen] = React.useState(false)

  const selectedTags = useMemo(
    () => tags?.filter((tag) => value?.includes(tag.id)),
    [tags, value],
  )

  const handleChange = (value: string[] | null) => {
    if (value === null || value.length === 0) {
      onChange([])
    } else {
      onChange(value)
    }
  }

  const triggerLabel =
    selectedTags && selectedTags.length > 0
      ? (selectedLabel ??
        (selectedTags.length > 1
          ? `${selectedTags.length} tags`
          : selectedTags[0].name))
      : (emptyLabel ?? 'Select tags')

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild={true}>
        {triggerVariant === 'field' ? (
          <button
            type="button"
            className={twMerge(
              TEXT_INPUT_CLASSNAME,
              'flex items-center justify-between gap-2 text-left',
              triggerClassName,
            )}
          >
            <span
              className={twMerge(
                'flex min-w-0 items-center gap-2 truncate',
                selectedTags && selectedTags.length > 0
                  ? 'text-primary-900 dark:text-primary-100'
                  : 'text-primary-500 dark:text-primary-400',
              )}
            >
              <HiOutlineTag
                size={16}
                className="flex-shrink-0 text-primary-500 dark:text-primary-400"
              />
              <span className="truncate">{triggerLabel}</span>
            </span>
            <HiChevronUpDown size={16} className="flex-shrink-0" />
          </button>
        ) : (
          <Button
            type="button"
            variant="soft-compact"
            className={twMerge(
              'flex items-center active:scale-100',
              'text-[13px]',
              triggerClassName,
            )}
          >
            {selectedTags && selectedTags.length > 0 ? (
              <span>{triggerLabel}</span>
            ) : (
              <>
                <HiOutlineTag
                  size={16}
                  className="text-primary-700 dark:text-primary-300"
                />
                <span>{triggerLabel}</span>
              </>
            )}
          </Button>
        )}
      </ComboboxTrigger>
      {/*
        not sure why, but when using this component inside a Dialog,
        the Dialog would double render which would cause it to mount twice
        and thus do its animation twice; this is a workaround to delay
        mounting until actually needed;
        TODO: figure out why this is happening and fix it
        */}
      {open && (
        <TagComboboxContent
          comboboxContentProps={comboboxContentProps}
          value={value}
          multiple={true}
          onChange={handleChange}
          placeholder={searchPlaceholder ?? 'Search tags...'}
          tagType={tagType}
        />
      )}
    </Combobox>
  )
}

TagCombobox.displayName = 'TagCombobox'
TagComboboxMulti.displayName = 'TagComboboxMulti'
